import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { cn } from "@/lib/utils";
import { Link, LinkProps } from "@tanstack/react-router";
import {
  ArrowLeftRightIcon,
  ClipboardListIcon,
  CrownIcon,
  DollarSignIcon,
  FileTextIcon,
  ReceiptIcon,
  ReceiptTextIcon,
  Grid3x3Icon,
  TruckIcon,
} from "lucide-react";
import { useTenant } from "@/app/Organisations/Hooks/useTenant";
import { BuildingOffice2Icon } from "@heroicons/react/20/solid";
import { FullPageScreen } from "@/lib/Components/Screens/FullPageScreen";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";

type SettingButton = {
  title: string;
  description: string;
  to: LinkProps["to"];
  icon: any;
  iconForeground: string;
  iconBackground: string;
  comingSoon?: boolean;
  adminOnly?: boolean;
};

export function ReportSelectionScreen() {
  const { isAdmin } = useTenant();
  const t = useTranslations("report");

  const generalReports: SettingButton[] = [
    {
      title: t("bookings"),
      description: t("bookingsDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/bookings",
      icon: CalendarDaysIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
    },
    {
      title: t("availableRelocations"),
      description: t("availableRelocationsDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocations",
      icon: ArrowLeftRightIcon,
      iconForeground: "text-green-700",
      iconBackground: "bg-green-50",
    },
    {
      title: t("relocations"),
      description: t("relocationsDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/relocation-flow",
      icon: Grid3x3Icon,
      iconForeground: "text-purple-700",
      iconBackground: "bg-purple-50",
    },
  ];

  const adminReports: SettingButton[] = [
    {
      title: t("invoices"),
      description: t("invoicesDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/invoices",
      icon: FileTextIcon,
      iconForeground: "text-blue-700",
      iconBackground: "bg-blue-50",
      adminOnly: true,
    },
    {
      title: t("workOrders"),
      description: t("workOrdersDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/work-orders",
      icon: ClipboardListIcon,
      iconForeground: "text-indigo-700",
      iconBackground: "bg-indigo-50",
      adminOnly: true,
    },
    {
      title: t("assignments"),
      description: t("assignmentsDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/assignments",
      icon: TruckIcon,
      iconForeground: "text-lime-700",
      iconBackground: "bg-lime-50",
      adminOnly: true,
    },
    {
      title: t("suppliers"),
      description: t("suppliersDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/suppliers",
      icon: BuildingOffice2Icon,
      iconForeground: "text-amber-700",
      iconBackground: "bg-amber-50",
      adminOnly: true,
    },
    {
      title: t("proDriverSuppliers"),
      description: t("proDriverSuppliersDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/pro-driver-suppliers",
      icon: ClipboardListIcon,
      iconForeground: "text-rose-700",
      iconBackground: "bg-rose-50",
      adminOnly: true,
    },
    {
      title: t("sales"),
      description: t("salesDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/sales",
      icon: DollarSignIcon,
      iconForeground: "text-orange-700",
      iconBackground: "bg-orange-50",
      adminOnly: true,
    },
    {
      title: t("receivablesReconciliation"),
      description: t("receivablesReconciliationDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/receivables-reconciliation",
      icon: ReceiptIcon,
      iconForeground: "text-emerald-700",
      iconBackground: "bg-emerald-50",
      adminOnly: true,
    },
    {
      title: t("basReceipts"),
      description: t("basReceiptsDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/receipts",
      icon: ReceiptTextIcon,
      iconForeground: "text-cyan-700",
      iconBackground: "bg-cyan-50",
      adminOnly: true,
    },
    {
      title: t("admin"),
      description: t("adminDescription"),
      to: "/$tenant/$orgId/supplier/{-$supplierId}/reports/admin",
      icon: CrownIcon,
      iconForeground: "text-pink-700",
      iconBackground: "bg-pink-50",
      adminOnly: true,
    },
  ];

  function ReportSection({
    title,
    reports,
  }: {
    title: string;
    reports: SettingButton[];
  }) {
    return (
      <div className="mb-8">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
        <div className="divide-y divide-gray-200 overflow-hidden rounded-lg bg-gray-200 shadow-sm sm:grid sm:grid-cols-2 sm:gap-px sm:divide-y-0">
          {reports.map((action, actionIdx) => (
            <div
              key={action.title}
              className={cn(
                actionIdx === 0
                  ? "rounded-tl-lg rounded-tr-lg sm:rounded-tr-none"
                  : "",
                actionIdx === 1 ? "sm:rounded-tr-lg" : "",
                actionIdx === reports.length - 2 ? "sm:rounded-bl-lg" : "",
                actionIdx === reports.length - 1
                  ? "rounded-bl-lg rounded-br-lg sm:rounded-bl-none"
                  : "",
                "group relative bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500",
              )}
            >
              <div className="">
                <span
                  className={cn(
                    action.iconBackground,
                    action.iconForeground,
                    "inline-flex rounded-lg p-3 ring-4 ring-white",
                  )}
                >
                  <action.icon className="h-6 w-6" aria-hidden="true" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-base font-semibold leading-6 text-gray-900">
                  <Link
                    from="/$tenant/$orgId/supplier/{-$supplierId}"
                    // action.to is a concrete absolute scoped route; cast to a
                    // valid relative literal so the typed `from`+`params` pair
                    // checks (absolute `to` ignores `from` at runtime).
                    to={action.to as "."}
                    params={(prev) => prev}
                    className="focus:outline-hidden"
                    disabled={action.comingSoon}
                  >
                    <span className="absolute inset-0" aria-hidden="true" />
                    {action.title}
                  </Link>

                  {action.comingSoon ? (
                    <Badge className="ml-2">Coming soon</Badge>
                  ) : null}
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  {action.description}
                </p>
              </div>
              <span
                className="pointer-events-none absolute right-6 top-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true"
              >
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <FullPageScreen>
      <ReportSection title={t("generalReports")} reports={generalReports} />
      {isAdmin ? (
        <ReportSection title={t("adminReports")} reports={adminReports} />
      ) : null}
    </FullPageScreen>
  );
}
